import { create } from 'zustand'

// Stores
import { useUserStore } from '@/stores/user'
import { useDevicesStoreActions } from '@/stores/devices'
import { useMainStore } from '@/stores/main'
import {
  FEATURES,
  isFeatureDisabled,
  isFeatureEnabled,
} from '@/stores/features'
import { useUserSettingsStore } from '@/stores/userSettings'
import { useUiStoreActions } from '@/stores/ui'
import { useRoomSessionStore } from '@/stores/roomSession'

// Utils
import { logger } from '@/logger/createLogger'
import { buildRoomPath, typeSafeFilterObject } from '@/helpers/utils'
import { LAYOUT_MAP, mapLayoutPositions } from '@/helpers/layouts'
import {
  CHANNEL_TYPE,
  ENV_VARS,
  INCOMING_CALL_CONTEXT,
} from '@/helpers/constants'

// Types
import type {
  CallJoinedEventParams,
  CallUpdatedEventParams,
  CallLayoutChangedEventParams,
  MemberJoinedEventParams,
  MemberLeftEventParams,
  MemberTalkingEventParams,
  MemberUpdatedEventParams,
  CallSession,
  OverlayMap,
  VideoPosition,
  RoomSessionScreenShare,
  InternalMemberEntity,
  DialParams,
} from '@signalwire/client'
import type { StateCreator } from 'zustand'
import type {
  AudioConstraints,
  CallError,
  CallState,
  ChannelType,
  RedirectPath,
  RoomMemberEntity,
  RoomMembersMap,
} from '@/helpers/types'
import type { Layout, LayoutName, LayoutPosition } from '@/helpers/layouts'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { RoomContext } from '@/helpers/room'
import { useAuthStore } from '@/stores/auth'
import { useDevicesStore } from '@/stores/devices'

interface RoomState {
  memberId?: string
  roomLocked: boolean
}

interface CallJoinOptions {
  channel?: ChannelType | undefined
  context: RoomContext
  name: string
  redirect?: RedirectPath | undefined
  rootElement: HTMLElement
}

interface UpdateMemberBoolean {
  memberId: string
  value: boolean
}

interface UpdateMemberValue {
  memberId: string
  value: number
}

interface Actions {
  actions: {
    _cleanUp: () => void
    _getMemberStateById: (memberId: string) => RoomMemberEntity
    _getSelfMemberState: () => RoomMemberEntity
    _getVideoConstraints: () => MediaTrackConstraints | undefined
    _onDestroy: (roomSession: CallSession) => void
    _onJoined: (event: CallJoinedEventParams) => void
    _onLayoutChanged: (event: CallLayoutChangedEventParams) => void
    _onMediaConnected: () => void
    _onMemberJoined: (event: MemberJoinedEventParams) => void
    _onMemberLeft: (event: MemberLeftEventParams) => void
    _onMemberTalking: (event: MemberTalkingEventParams) => void
    _onMemberUpdated: (event: MemberUpdatedEventParams) => void
    _onUpdated: (event: CallUpdatedEventParams) => void
    _upsertMemberInStore: (member: RoomMemberEntity) => void
    _upsertMembersInStore: (members: RoomMemberEntity[]) => void
    addVideoHandler: () => void
    audioMuteAllHandler: (value: boolean) => Promise<void>
    audioMuteHandler: (memberId: string) => void
    getLayouts: () => Promise<Layout[]>
    getMembersState: (memberMap: RoomMembersMap) => RoomMemberEntity[]
    handRaiseHandler: (memberId: string) => void
    incrementElapsedTime: () => void
    joinRoom: (options: CallJoinOptions) => Promise<boolean>
    leaveRoom: () => Promise<{ error: unknown; success: boolean }>
    lockRoomHandler: () => boolean
    partialUpdateCamera: ({
      constraints,
      memberId,
    }: {
      constraints: MediaTrackConstraints
      memberId: string
    }) => Promise<void>
    partialUpdateMicrophone: ({
      constraints,
      memberId,
    }: {
      constraints: AudioConstraints
      memberId: string
    }) => Promise<void>
    resetElapsedTime: () => void
    screenShareHandler: () => Promise<void>
    setAddressId: (addressId: string) => void
    setCallError: (error: CallError) => void
    setCallState: (state: CallState) => void
    setLayout: (layout: LayoutName) => void
    setLayoutPosition: (positions: VideoPosition) => void
    setMicrophoneVolume: (params: UpdateMemberValue) => Promise<void>
    setMirrorLocalOverlay: (mirror: boolean) => void
    setNoiseGate: (params: UpdateMemberValue) => Promise<void>
    setRedirect: (redirect: RedirectPath) => void
    setRemoveBackgroundNoise: (params: UpdateMemberBoolean) => void
    setRoomName: (roomName: string) => void
    setRootElementById: (elementId: string) => HTMLElement | null
    setSpeakerVolume: (params: UpdateMemberValue) => Promise<void>
    setUserParameters: (userParameters: Record<string, unknown>) => void
    updateCameraHandler: (deviceId: string) => Promise<void>
    updateMicrophoneHandler: (deviceId: string) => Promise<void>
    updateSpeakerHandler: (deviceId: string) => Promise<void>
    videoMuteHandler: (memberId: string) => void
  }
}

interface State {
  addressId: string
  callError: CallError
  callState: CallState
  cameraId: string | null
  elapsedTimeSecs: number
  hasCamera: boolean
  hasMicrophone: boolean
  hasSpeaker: boolean
  isAudioOnly: boolean
  layout: LayoutName | ''
  layoutPosition: string
  layoutPositions: LayoutPosition[]
  layouts: Layout[]
  mediaConnected: boolean
  memberId: string
  membersMap: Map<string, RoomMemberEntity>
  microphoneId: string | null
  mirrorLocalOverlay: boolean
  overlayMap: OverlayMap | undefined
  path: string
  redirect: RedirectPath
  roomLocked: boolean
  roomName: string
  rootElement: HTMLElement | null
  screenShareStream: RoomSessionScreenShare | null
  sharingScreen: boolean
  speakerId: string | null
  userParameters: Record<string, unknown>
}

type Store = Actions & State

const initialState: State = {
  addressId: '',
  callError: { type: '' },
  callState: 'ready',
  cameraId: '',
  elapsedTimeSecs: 0,
  hasCamera: true,
  hasMicrophone: true,
  hasSpeaker: true,
  isAudioOnly: true,
  layout: '',
  layoutPosition: '',
  layoutPositions: [],
  layouts: [],
  mediaConnected: false,
  memberId: '',
  membersMap: new Map(),
  microphoneId: '',
  mirrorLocalOverlay: true,
  overlayMap: new Map(),
  path: '',
  redirect: '/',
  roomLocked: false,
  roomName: '',
  rootElement: null,
  screenShareStream: null,
  sharingScreen: false,
  speakerId: '',
  userParameters: {},
}

const partialMapMember = (member: InternalMemberEntity) => {
  // FIXME: remove the `@ts-expect-error` once the SDK is updated to include the following properties
  // @ts-expect-error - there is no `remove_background_noise` in the `InternalMemberEntity` type
  const removeBackgroundNoise = member.remove_background_noise as boolean

  return {
    ...('remove_background_noise' in member && { removeBackgroundNoise }),

    ...('address_id' in member && { addressId: member.address_id }),
    ...('audio_muted' in member && { audioMuted: member.audio_muted }),
    ...('auto_gain' in member && { autoGain: member.auto_gain }),
    ...('call_id' in member && { callId: member.call_id }),
    ...('current_position' in member && {
      currentPosition: member.current_position,
    }),
    ...('deaf' in member && { deaf: member.deaf }),
    ...('echo_cancellation' in member && {
      echoCancellation: member.echo_cancellation,
    }),
    ...('handraised' in member && { handRaised: member.handraised }),
    ...('member_id' in member && { id: member.member_id }),
    ...('member_id' in member && { memberId: member.member_id }),
    ...('meta' in member && { meta: member.meta }),
    ...('input_sensitivity' in member && {
      noiseGate: member.input_sensitivity,
    }),
    ...('input_volume' in member && { microphoneVolume: member.input_volume }),
    ...('name' in member && { name: member.name }),
    ...('node_id' in member && { nodeId: member.node_id }),
    ...('noise_suppression' in member && {
      noiseSuppression: member.noise_suppression,
    }),
    ...('output_volume' in member && { speakerVolume: member.output_volume }),
    ...('parent_id' in member && { parentId: member.parent_id }),
    ...('requested_position' in member && {
      requestedPosition: member.requested_position,
    }),
    ...('room_id' in member && { roomId: member.room_id }),
    ...('room_session_id' in member && {
      roomSessionId: member.room_session_id,
    }),
    ...('subscriber_id' in member && { subscriberId: member.subscriber_id }),
    ...('talking' in member && { talking: member.talking }),
    ...('type' in member && { type: member.type }),
    ...('video_muted' in member && { videoMuted: member.video_muted }),
    ...('visible' in member && { visible: member.visible }),
  } satisfies Partial<RoomMemberEntity>
}

const mapMemberToUserMetaData = (member: RoomMemberEntity) => {
  return {
    userId: member.subscriberId,
    userName: member.name,
  }
}

const mapMembers = (members: InternalMemberEntity[]): RoomMemberEntity[] => {
  return members.map(mapMember)
}

const mapMember = (member: InternalMemberEntity): RoomMemberEntity => ({
  // FIXME: remove the `@ts-expect-error` once the SDK is updated to include the following properties

  // @ts-expect-error - there is no `remove_background_noise` in the `InternalMemberEntity` type
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  removeBackgroundNoise: member.remove_background_noise ?? false,

  addressId: member.address_id,
  audioMuted: member.audio_muted,
  autoGain: member.auto_gain,
  callId: member.call_id,
  currentPosition: member.current_position,
  deaf: member.deaf,
  echoCancellation: member.echo_cancellation,
  handRaised: member.handraised,
  id: member.member_id,
  memberId: member.member_id,
  meta: member.meta,
  microphoneVolume: member.input_volume,
  name: member.name,
  nodeId: member.node_id,
  noiseGate: member.input_sensitivity,
  noiseSuppression: member.noise_suppression,
  parentId: member.parent_id,
  requestedPosition: member.requested_position,
  roomId: member.room_id,
  roomSessionId: member.room_session_id,
  speakerVolume: member.output_volume,
  subscriberId: member.subscriber_id,
  talking: member.talking ?? false,
  type: member.type,
  videoMuted: member.video_muted,
  visible: member.visible,
})

const stateCreatorFn: StateCreator<Store> = (set, get) => ({
  ...initialState,
  actions: {
    _cleanUp: () => {
      const { releaseDevices } = useDevicesStoreActions()
      const { resetRoomPanelState } = useUiStoreActions()
      releaseDevices()
      resetRoomPanelState()

      // This will clear the `memberState`, which means we're ready to join another room
      set(initialState)
      logger.debug('>> room store _cleanUp done')
    },
    _getMemberStateById: (memberId: string) => {
      const { membersMap } = get()
      const memberEntity = membersMap.get(memberId)

      if (!memberEntity) {
        throw new Error(`Member with id ${memberId} not found`)
      }
      return memberEntity
    },
    _getSelfMemberState: () => {
      const { memberId, actions } = get()
      const { _getMemberStateById } = actions
      if (!memberId) {
        throw new Error('Self Member ID is not defined')
      }
      const selfMemberEntity = _getMemberStateById(memberId)
      return selfMemberEntity
    },
    _getVideoConstraints: () => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()
      return roomSession.localVideoTrack?.getConstraints()
    },
    _onDestroy: roomSession => {
      const { callState, redirect } = get()
      const {
        _onMemberJoined,
        _onMemberUpdated,
        _onMemberLeft,
        _onMemberTalking,
        setCallState,
      } = get().actions
      const router = useAuthStore.getState().router

      // FIXME: The SDK emits the room object on `destroy` event which contains
      // properties including `cause` and `leaveReason`.
      // The `leaveReason` is undefined at the moment and `cause` does not exist
      // on the TS interface.
      // The SDK is missing the `room.left` event as well. This needs to be
      // discussed with the team and fixed.
      // TODO: Inform user about the room left reason
      logger.debug('>> destroy room reason', roomSession.leaveReason)

      roomSession.off('member.joined', _onMemberJoined)
      roomSession.off('member.updated', _onMemberUpdated)
      roomSession.off('member.left', _onMemberLeft)
      roomSession.off('member.talking', _onMemberTalking)

      // TODO: the SDK can emit the `destroy` event before call.start() has resolved/rejected
      // the conditional check allows the call.start to return a rejected promise so it can be handled by the caller
      if (callState === 'joined') {
        // navigate back to the redirect path
        void router?.navigate({ to: redirect })
        setCallState('leaving')
      }
      logger.debug('>> destroy room done')
    },
    _onJoined: event => {
      const { _upsertMembersInStore } = get().actions
      const { upsertManyUserMetaData } = useUserStore.getState().actions

      const roomState = {
        memberId: event.member_id,
        roomLocked: event.room_session.locked,
      }
      set({ ...roomState })

      const mappedMembers = mapMembers(event.room_session.members)
      _upsertMembersInStore(mappedMembers)

      upsertManyUserMetaData(mappedMembers.map(mapMemberToUserMetaData))
    },
    _onLayoutChanged: event => {
      const { memberId } = get()
      set({
        layout: event.layout.id as LayoutName,
        layoutPosition:
          event.layout.layers.find(layer => layer.member_id === memberId)
            ?.position || '',
        layoutPositions: mapLayoutPositions(event.layout.layers),
      })
    },
    _onMediaConnected: () => {
      set({ mediaConnected: true })
    },
    _onMemberJoined: event => {
      const { _upsertMemberInStore } = get().actions
      const { upsertUserMetaData } = useUserStore.getState().actions

      const mappedMember = mapMember(event.member)
      _upsertMemberInStore(mappedMember)

      upsertUserMetaData(mapMemberToUserMetaData(mappedMember))
    },
    _onMemberLeft: event => {
      const { membersMap } = get()
      const updatedMembersMap = new Map(membersMap)
      updatedMembersMap.delete(event.member.member_id!)
      set({ membersMap: updatedMembersMap })
    },
    _onMemberTalking: event => {
      const { actions, membersMap } = get()
      const { _upsertMemberInStore } = actions
      const { upsertUserMetaData } = useUserStore.getState().actions

      const existingMember = membersMap.get(event.member.member_id)
      if (existingMember) {
        const updatedMember = {
          ...existingMember,
          talking: event.member.talking,
        }
        _upsertMemberInStore(updatedMember)

        upsertUserMetaData(mapMemberToUserMetaData(updatedMember))
      }
    },
    _onMemberUpdated: event => {
      const { actions, membersMap } = get()
      const { _upsertMemberInStore } = actions
      const { upsertUserMetaData } = useUserStore.getState().actions

      const partialMemberUpdate = partialMapMember(event.member)
      const existingMember = membersMap.get(event.member.member_id)
      if (existingMember) {
        const updatedMember = {
          ...existingMember,
          ...partialMemberUpdate,
        }
        _upsertMemberInStore(updatedMember)
        upsertUserMetaData(mapMemberToUserMetaData(updatedMember))
      }
    },
    _onUpdated: event => {
      const roomState: RoomState = {
        roomLocked: event.room_session.locked,
      }
      set({ ...roomState })
    },
    _upsertMemberInStore: member => {
      const { _upsertMembersInStore } = get().actions
      _upsertMembersInStore([member])
    },
    _upsertMembersInStore: members => {
      const { membersMap } = get()
      const updatedMembersMap = new Map(membersMap)

      members.forEach(member => {
        updatedMembersMap.set(member.memberId, member)
      })

      set({ membersMap: updatedMembersMap })
    },
    addVideoHandler: () => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { isAudioOnly, actions } = get()
      const { _upsertMemberInStore } = actions
      const { setAttachedTo } = useRoomSessionStore.getState().actions

      const { _getSelfMemberState } = get().actions
      const selfMemberEntity = _getSelfMemberState()

      // In case of audio-only call, we need to update the media and renegotiate
      if (isAudioOnly) {
        void roomSession.updateMedia({ video: { direction: 'sendrecv' } })
        set({ isAudioOnly: false })

        // The server does not allow reattach if we add video to an audio-only call
        setAttachedTo(undefined)

        // enable video
        const updatedSelfEntity = { ...selfMemberEntity, videoMuted: false }
        _upsertMemberInStore(updatedSelfEntity)
      }
    },
    audioMuteAllHandler: (value: boolean) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      if (value) {
        return roomSession.audioMute({ memberId: 'all' })
      } else {
        return roomSession.audioUnmute({ memberId: 'all' })
      }
    },
    audioMuteHandler: memberId => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { _upsertMemberInStore, _getMemberStateById } = get().actions

      const memberEntity = _getMemberStateById(memberId)

      // optimistically update the member state
      const updatedMemberEntity = {
        ...memberEntity,
        audioMuted: !memberEntity.audioMuted,
      } satisfies RoomMemberEntity
      _upsertMemberInStore(updatedMemberEntity)

      if (memberEntity.audioMuted) {
        void roomSession.audioUnmute({ memberId }).catch(error => {
          logger.error('Error unmuting audio:', error)
          // If the unmute fails, we need to set the member state back to muted
          _upsertMemberInStore({ ...memberEntity, audioMuted: true })
          throw error
        })
      } else {
        void roomSession.audioMute({ memberId }).catch(error => {
          logger.error('Error muting audio:', error)
          // If the mute fails, we need to set the member state back to unmuted
          _upsertMemberInStore({ ...memberEntity, audioMuted: false })
          throw error
        })
      }
    },
    getLayouts: async () => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { layouts } = await roomSession.getLayouts()

      const mappedLayouts = layouts
        .map(layout => {
          return LAYOUT_MAP[layout as LayoutName]
        })
        .filter(Boolean)

      set({
        layouts: mappedLayouts,
      })
      return mappedLayouts
    },
    getMembersState: membersMap => {
      return Array.from(membersMap.values())
    },
    handRaiseHandler: memberId => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { _upsertMemberInStore, _getMemberStateById } = get().actions
      const member = _getMemberStateById(memberId)

      const updatedMemberEntity = {
        ...member,
        handRaised: !member.handRaised,
      } satisfies RoomMemberEntity
      _upsertMemberInStore(updatedMemberEntity)

      if (member.handRaised) {
        void roomSession
          .setRaisedHand({ memberId, raised: false })
          .catch(error => {
            logger.error('Error removing hand raise:', error)
            // restore the member state to raised on failure
            _upsertMemberInStore({
              ...member,
              handRaised: true,
            })
            throw error
          })
      } else {
        void roomSession
          .setRaisedHand({ memberId, raised: true })
          .catch(error => {
            logger.error('Error raising hand:', error)
            // If the raise fails, we need to set the member state back to unraised
            _upsertMemberInStore({
              ...member,
              handRaised: false,
            })
            throw error
          })
      }
    },
    incrementElapsedTime: () => {
      const { elapsedTimeSecs } = get()
      set({ elapsedTimeSecs: elapsedTimeSecs + 1 })
    },
    joinRoom: async (options: CallJoinOptions) => {
      const { context, name, channel, redirect, rootElement = null } = options
      const { client } = useMainStore.getState()
      const { join } = useRoomSessionStore.getState().actions
      const { mirrorLocalOverlay } = get()
      const {
        _onDestroy,
        _onJoined,
        _onLayoutChanged,
        _onMediaConnected,
        _onMemberJoined,
        _onMemberLeft,
        _onMemberTalking,
        _onMemberUpdated,
        _onUpdated,
        getLayouts,
        setCallState,
      } = get().actions

      const {
        getPreferredMicrophoneDeviceForDialing,
        getPreferredVideoDeviceForDialing,
        getPreferredSpeakerDeviceId,
      } = useUserSettingsStore.getState().actions
      const { cameraId, microphoneId, speakerId } = useDevicesStore.getState()

      const path = buildRoomPath(context, name, channel, redirect)

      set({ isAudioOnly: channel !== CHANNEL_TYPE.VIDEO, path })

      setCallState('joining')

      if (!path) {
        logger.error('Cannot join using an empty address')
        return false
      }

      if (!client) {
        throw new Error('Client is not defined')
      }

      // TODO: Set a node_id for steering?
      const steeringId = ''
      const { getUserVariables } = useUserStore.getState().actions

      const preferredMicrophone = getPreferredMicrophoneDeviceForDialing(path)
      let audioConstraints: MediaTrackConstraints | boolean
      if (microphoneId) {
        audioConstraints = { deviceId: { exact: microphoneId } }
      } else if (
        typeof preferredMicrophone === 'object' &&
        preferredMicrophone !== null
      ) {
        audioConstraints = preferredMicrophone
      } else {
        audioConstraints = true
      }

      const joinParams: DialParams = {
        audio: audioConstraints,
        mirrorLocalVideoOverlay: mirrorLocalOverlay,
        nodeId: steeringId,
        rootElement: rootElement,
        to: path,
        video: cameraId
          ? { deviceId: { exact: cameraId } }
          : getPreferredVideoDeviceForDialing(path),
        // For the incoming calls, always answer with audio-only
        ...(context === INCOMING_CALL_CONTEXT && {
          negotiateVideo: false,
          video: false,
        }),
      }

      const userVariablesIsDisabled = isFeatureDisabled(FEATURES.USER_VARIABLES)
      if (!userVariablesIsDisabled) {
        const userVariables = await getUserVariables({ channel })

        // User parameters (e.g. from the C2CButton)
        // are passed straight through to the join
        joinParams.userVariables = { ...userVariables, ...get().userParameters }
      }

      const call = await join(
        { ...joinParams },
        {
          onDestroy: _onDestroy,
          onJoined: _onJoined,
          onLayoutChanged: _onLayoutChanged,
          onMediaConnected: _onMediaConnected,
          onUpdated: _onUpdated,
        },
      )
      if (!call) {
        throw new Error('Call is not defined')
      }

      call.on('member.joined', _onMemberJoined)
      call.on('member.updated', _onMemberUpdated)
      call.on('member.left', _onMemberLeft)
      call.on('member.talking', _onMemberTalking)
      set({ overlayMap: call.overlayMap })

      setCallState('joined')

      // Update speaker with persisted speakerId if available
      const speakerDeviceId =
        speakerId || getPreferredSpeakerDeviceId() || 'default'
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()
      void roomSession.updateSpeaker({ deviceId: speakerDeviceId })

      // Fetch and store room layouts
      void getLayouts()

      // Expose the roomSession to be used from the console
      if (
        ENV_VARS.IS_DEVELOPMENT ||
        ENV_VARS.IS_TESTING ||
        isFeatureEnabled(FEATURES.WINDOW_EXPOSE_ROOM_SESSION)
      ) {
        window.__roomSession = call
      }

      return true
    },
    leaveRoom: async () => {
      const { callState } = get()
      const { leave } = useRoomSessionStore.getState().actions

      if (callState === 'ready') {
        logger.debug(
          'XXXX: Member state is ready. Should not be in a call. Not calling hangup',
        )
        return { error: undefined, success: true }
      }

      return await leave()
    },
    lockRoomHandler: () => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { roomLocked } = get()

      if (roomLocked) {
        void roomSession.unlock()
      } else {
        void roomSession.lock()
      }
      set({ roomLocked: !roomLocked })

      return !roomLocked
    },
    partialUpdateCamera: async constraints => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const currentVideoConstraints = get().actions._getVideoConstraints()

      const mergedConstraints = {
        ...currentVideoConstraints,
        ...constraints,
      } satisfies MediaTrackConstraints

      await roomSession.updateCamera(mergedConstraints)
    },
    partialUpdateMicrophone: async ({ constraints, memberId }) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      return roomSession.setAudioFlags({
        ...constraints,
        memberId,
      })
    },
    resetElapsedTime: () => {
      set({ elapsedTimeSecs: 0 })
    },
    screenShareHandler: async (): Promise<void> => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { sharingScreen, screenShareStream } = get()

      try {
        if (sharingScreen) {
          // If a local screenshare exists, then stop it
          set({ sharingScreen: false })
          if (screenShareStream) {
            await screenShareStream.leave()
          }
          set({ screenShareStream: null })
        } else {
          // Initialize a screenshare media stream
          set({ sharingScreen: true })
          const screenShare = await roomSession.startScreenShare()
          set({ screenShareStream: screenShare })

          screenShare.once('destroy', () => {
            set({ screenShareStream: null, sharingScreen: false })
          })
        }
      } catch (error) {
        logger.error('Screenshare Error: ', error)
        set({ screenShareStream: null, sharingScreen: false })
      }
    },
    setAddressId: (addressId: string) => {
      set({
        addressId,
      })
    },
    setCallError: (error: CallError) => {
      set({ callError: error })
    },
    setCallState: (state: CallState) => {
      set({ callState: state })
    },
    setLayout: (layout: LayoutName) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      void roomSession.setLayout({ name: layout })
      set({ layout })
    },
    setLayoutPosition: position => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      void roomSession.setPositions({ positions: { self: position } })
      set({ layoutPosition: position })
    },
    setMicrophoneVolume: ({ memberId, value }) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      return roomSession.setInputVolume({
        memberId: memberId,
        volume: value,
      })
    },
    setMirrorLocalOverlay: (mirror: boolean) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      roomSession.localVideoOverlay?.setMirror(mirror)
      set({ mirrorLocalOverlay: mirror })
    },
    setNoiseGate: ({ memberId, value }) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      return roomSession.setInputSensitivity({ memberId, value })
    },
    setRedirect: (redirect: RedirectPath) => {
      set({ redirect })
    },
    setRemoveBackgroundNoise: ({ memberId, value }) => {
      const { _getMemberStateById, _upsertMemberInStore } = get().actions

      const memberEntity = _getMemberStateById(memberId)

      const updatedMemberEntity = {
        ...memberEntity,
        removeBackgroundNoise: value,
      } satisfies RoomMemberEntity

      _upsertMemberInStore(updatedMemberEntity)
    },
    setRoomName: (roomName: string) => {
      set({
        roomName,
      })
    },
    setRootElementById: (elementId: string) => {
      let rootElement: HTMLElement | null = null

      if (elementId) {
        rootElement = document.getElementById(elementId)
      }
      set({ rootElement })
      return rootElement
    },
    setSpeakerVolume: ({ memberId, value }) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      return roomSession.setOutputVolume({
        memberId: memberId,
        volume: value,
      })
    },
    setUserParameters: (userParameters: Record<string, unknown>) => {
      set({
        userParameters,
      })
    },
    updateCameraHandler: async (deviceId: string) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { _getSelfMemberState } = get().actions
      const selfMember = _getSelfMemberState()

      // TODO: Handle errors?
      await roomSession.updateCamera({ deviceId: { exact: deviceId } })

      // immediately stop the outbound video if the self-user is muted
      if (selfMember.videoMuted) {
        roomSession.stopOutboundVideo()
      }
    },
    updateMicrophoneHandler: async (deviceId: string) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { _getSelfMemberState } = get().actions
      const { audioMuted } = _getSelfMemberState()

      await roomSession.updateMicrophone({ deviceId: { exact: deviceId } })

      // immediately stop the outbound audio if the self-user is muted
      if (audioMuted) {
        roomSession.stopOutboundAudio()
      }
    },
    updateSpeakerHandler: async (deviceId: string) => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()
      await roomSession.updateSpeaker({ deviceId })
    },
    videoMuteHandler: memberId => {
      const roomSession = useRoomSessionStore
        .getState()
        .actions.getRoomSession()

      const { _getMemberStateById, _upsertMemberInStore } = get().actions
      const member = _getMemberStateById(memberId)

      // optimistically update the member state
      const updatedMemberEntity = {
        ...member,
        videoMuted: !member.videoMuted,
      } satisfies RoomMemberEntity
      _upsertMemberInStore(updatedMemberEntity)

      if (member.videoMuted) {
        void roomSession.videoUnmute({ memberId }).catch(error => {
          logger.error('Error unmuting video:', error)
          // If the unmute fails, we need to set the member state back to muted
          _upsertMemberInStore({
            ...member,
            videoMuted: true,
          })
          throw error
        })
      } else {
        void roomSession.videoMute({ memberId }).catch(error => {
          logger.error('Error muting video:', error)
          // If the mute fails, we need to set the member state back to unmuted
          _upsertMemberInStore({
            ...member,
            videoMuted: false,
          })
          throw error
        })
      }
    },
  },
})

const partialize = (state: State) => {
  type KeysToPersist = keyof Pick<State, 'mirrorLocalOverlay'>
  const persistKeys = ['mirrorLocalOverlay'] as const satisfies KeysToPersist[]

  return typeSafeFilterObject({ ...state }, key =>
    persistKeys.includes(key as KeysToPersist),
  ) as Store
}

export const useRoomStore = create<Store>()(
  persist<Store>(stateCreatorFn, {
    name: 'sw_puc_room_settings',
    partialize: state => partialize(state),
    storage: createJSONStorage(() => localStorage),
  }),
)

export const useRoomStoreActions = () => useRoomStore.getState().actions
